import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Modal, { MODAL_SIZE } from "components/Modal";
import { Spinner } from "components/Loader";
import { css } from "@emotion/react";
import { colors, fontSize, margins } from "style";
import { formatElapsedTime } from "utils";

/**
 * InterviewQuestionAIVideoStatusModal
 *
 * Generic status modal for AI interview question video lifecycle states
 * such as generating, uploading, and error feedback.
 *
 * @param {Object}     task
 * @param {Function}   onClose
 * @param {Function}   onCancel
 * @param {string}     titleContentMsgId
 * @param {string}     descriptionContentMsgId
 * @param {string}     supportingContentMsgId
 * @param {Object}     supportingContentValues
 * @param {boolean}    showLoader
 * @param {boolean}    showElapsedInHeader
 * @param {React.Node} statusContent
 */
const InterviewQuestionAIVideoStatusModal = ({
  task,
  onClose,
  onCancel,
  titleContentMsgId = "Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.Title",
  descriptionContentMsgId = "Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.Description",
  supportingContentMsgId,
  supportingContentValues,
  showLoader = true,
  showElapsedInHeader = false,
  statusContent,
  ...props
}) => {
  const elapsedTime = showElapsedInHeader ? formatElapsedTime(task?.submittedAt, task?.confirmedAt) : null;

  return (
    <Modal
      onClose={onClose}
      onDestructive={onCancel}
      title={<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Modal.Title" />}
      info={
        elapsedTime && (
          <FormattedMessage
            id="Missions.Roles.RoleVetting.Interviews.AIVideo.Meta.ElapsedTime.Header"
            values={{ value: elapsedTime }}
          />
        )
      }
      size={MODAL_SIZE.large}
      showSave={false}
      showSaveAsDestructable={false}
      isDestructable={true}
      showCancel={true}
      secondaryButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Hide"
      desctructableButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Cancel.Job"
      {...props}
    >
      <div css={styles.processing}>
        <h3 css={styles.title}>
          <FormattedMessage id={titleContentMsgId} />
        </h3>

        <p css={styles.description}>
          <FormattedMessage id={descriptionContentMsgId} />
        </p>

        {supportingContentMsgId && (
          <p css={styles.supporting}>
            <FormattedMessage id={supportingContentMsgId} values={supportingContentValues} />
          </p>
        )}

        {showLoader && (
          <div css={styles.spinnerContainer}>
            <Spinner size="3rem" />
          </div>
        )}

        <div css={styles.status}>{statusContent}</div>
      </div>
    </Modal>
  );
};

const styles = {
  processing: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.6rem;
    padding: 3rem 2rem;
    text-align: center;
  `,
  title: css`
    font-size: ${fontSize.large};
    font-weight: 600;
    color: ${colors.grayAnatomyBase};
    margin-bottom: 0.8rem;
  `,
  description: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    max-width: 50rem;
    line-height: 1.5;
    margin: 0;
  `,
  supporting: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyBase};
    margin: ${margins.none};
  `,
  spinnerContainer: css`
    margin: 1.6rem 0;
  `,
  status: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    font-weight: 500;
  `,
  meta: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight3};
    padding-top: 1.6rem;
  `,
};

InterviewQuestionAIVideoStatusModal.propTypes = {
  task: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  titleContentMsgId: PropTypes.string,
  descriptionContentMsgId: PropTypes.string,
  supportingContentMsgId: PropTypes.string,
  supportingContentValues: PropTypes.object,
  showLoader: PropTypes.bool,
  showElapsedInHeader: PropTypes.bool,
  statusContent: PropTypes.node.isRequired,
};

export default InterviewQuestionAIVideoStatusModal;
